"use client"

import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

const TooltipProvider = TooltipPrimitive.Provider

/**
 * Long-press support, so a tooltip is not hover-only.
 *
 * Radix opens a tooltip on hover and on focus, and a touch device has neither:
 * there is no pointer to rest, and tapping a button runs its action rather than
 * focusing it (iOS Safari in particular does not focus a tapped button). Every
 * tooltip in the app was therefore invisible on a phone. Most of ours only
 * repeat the trigger's own `aria-label`, so the loss was cosmetic, but not all
 * of them are (see the unverified-email tooltip in src/components/layout/header.tsx)
 * and nothing stops the next one from carrying real information.
 *
 * A press held past `TOUCH_HOLD_MS` opens it; lifting or dragging before that
 * cancels, so an ordinary tap still just activates the control underneath.
 * Nothing here runs for a mouse: the handlers are touch events.
 */
const TOUCH_HOLD_MS = 400

const TouchHoldContext = React.createContext<{
  onTouchStart: () => void
  onTouchEnd: () => void
  onTouchMove: () => void
} | null>(null)

const Tooltip = ({
  children,
  open,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>) => {
  const [touchOpen, setTouchOpen] = React.useState(false)
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancel = React.useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current)
      timer.current = null
    }
  }, [])

  React.useEffect(() => cancel, [cancel])

  const touchHold = React.useMemo(
    () => ({
      onTouchStart: () => {
        cancel()
        timer.current = setTimeout(() => setTouchOpen(true), TOUCH_HOLD_MS)
      },
      onTouchEnd: cancel,
      onTouchMove: cancel,
    }),
    [cancel],
  )

  // A caller that drives `open` itself keeps full control; long-press only fills
  // in for the uncontrolled case, which is every call site in the app today.
  const isControlled = open !== undefined
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setTouchOpen(next)
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange],
  )

  return (
    <TooltipPrimitive.Root
      open={isControlled ? open : touchOpen || undefined}
      defaultOpen={defaultOpen}
      onOpenChange={handleOpenChange}
      {...props}
    >
      <TouchHoldContext.Provider value={isControlled ? null : touchHold}>
        {children}
      </TouchHoldContext.Provider>
    </TooltipPrimitive.Root>
  )
}
Tooltip.displayName = "Tooltip"

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ onTouchStart, onTouchEnd, onTouchMove, ...props }, ref) => {
  const touchHold = React.useContext(TouchHoldContext)
  return (
    <TooltipPrimitive.Trigger
      ref={ref}
      onTouchStart={(event) => {
        onTouchStart?.(event)
        touchHold?.onTouchStart()
      }}
      onTouchEnd={(event) => {
        onTouchEnd?.(event)
        touchHold?.onTouchEnd()
      }}
      onTouchMove={(event) => {
        onTouchMove?.(event)
        touchHold?.onTouchMove()
      }}
      {...props}
    />
  )
})
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
